// Yandex Metrika — клиентский адаптер.
// Делает ровно две вещи:
//   1) install() — подгружает mc.yandex.ru/metrika/tag.js, инициализирует счётчик
//      (нужно чтобы Метрика поставила _ym_uid cookie и трекала автоматические pageviews).
//      Дополнительно ловит yclid из URL в свой namespace localStorage.
//   2) getContext() — отдаёт { clientId, yclid } для POST /api/analytics/track.
//
// БЕЗ mirror. Все custom-ивенты летят на наш бэк (POST /api/analytics/track),
// а оттуда server-adapter уже шлёт в Метрику через Measurement Protocol.

const YCLID_KEY = 'yandex.metrika.yclid';
const SCRIPT_URL = 'https://mc.yandex.ru/metrika/tag.js';

function loadMetrikaTag(counterId, initOpts) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.ym && typeof window.ym === 'function') {
    // Уже загружено — инициализируем счётчик (если ещё не был)
    try { window.ym(counterId, 'init', initOpts); } catch {}
    return;
  }

  // Стандартный загрузчик ym.
  /* eslint-disable */
  (function (m, e, t, r, i, k, a) {
    m[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments); };
    m[i].l = 1 * new Date();
    for (var j = 0; j < document.scripts.length; j++) {
      if (document.scripts[j].src === r) return;
    }
    k = e.createElement(t);
    a = e.getElementsByTagName(t)[0];
    k.async = 1;
    k.src = r;
    a.parentNode.insertBefore(k, a);
  })(window, document, 'script', `${SCRIPT_URL}?id=${counterId}`, 'ym');
  /* eslint-enable */

  try { window.ym(counterId, 'init', initOpts); } catch {}
}

function captureYclid() {
  if (typeof window === 'undefined') return;
  try {
    const u = new URL(window.location.href);
    const y = u.searchParams.get('yclid');
    if (y) localStorage.setItem(YCLID_KEY, y);
  } catch {}
}

function getYclidFromStorage() {
  if (typeof window === 'undefined') return null;
  try { return localStorage.getItem(YCLID_KEY) || null; } catch { return null; }
}

function getYmUidCookie() {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)_ym_uid=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// clientId через ym-очередь: сниппет-лоадер синхронно создаёт заглушку ym,
// которая буферизует вызовы до загрузки tag.js и исполняет их в момент
// готовности тэга. Один вызов getClientID при install() → кешированный промис;
// поллинг/повторные запросы на каждый ивент не нужны (это встроенный механизм
// Метрики, на него же полагаются reachGoal во всех SPA-интеграциях).
// Таймаут-страховка вшита в САМ промис: если tag.js не загрузится никогда
// (адблок), очередь ym не исполнится — промис settle-ится null один раз, и все
// последующие getContext резолвятся мгновенно из кэша (per-call race давал бы
// 3-секундный стопор на КАЖДЫЙ ивент у адблок-юзеров, включая клик «Оплатить»).
let clientIdReady = null;
function primeClientId(counterId, timeoutMs = 3000) {
  if (clientIdReady) return;
  clientIdReady = new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.ym !== 'function') return resolve(null);
    const t = setTimeout(() => resolve(null), timeoutMs);
    try {
      window.ym(counterId, 'getClientID', (id) => {
        clearTimeout(t);
        resolve(id ? String(id) : null);
      });
    } catch {
      clearTimeout(t);
      resolve(null);
    }
  });
}

export default function createMetrikaClientAdapter({
  // `name` — служебный ключ конфига (выбор адаптера в instantiateAdapters),
  // вырезаем, чтобы он не протекал через ...rest в опции ym('init').
  name: _name,
  counterId,
  webvisor = true,
  clickmap = true,
  trackLinks = true,
  accurateTrackBounce = true,
  ...rest
} = {}) {
  if (!counterId) throw new Error('Metrika client adapter requires counterId');

  // НЕ ставим `ssr:true` и `defer:true`: в этом режиме Метрика не шлёт автоматический
  // pageview при init и не детектит SPA-навигацию через History API — ждёт ручных
  // `ym('hit', url)` на каждый route change. Без хитов нет визитов → нет webvisor,
  // нет clickmap, и server-MP-ивенты не к чему привязать (goal не атрибутится).
  // Со стандартными опциями Метрика сама патчит pushState/replaceState и шлёт
  // первый хит при init — именно так работало до 06.05.
  const initOpts = {
    webvisor,
    clickmap,
    trackLinks,
    accurateTrackBounce,
    ...rest,
  };

  return {
    name: 'metrika',

    install() {
      loadMetrikaTag(counterId, initOpts);
      captureYclid();
      primeClientId(counterId);
    },

    async getContext() {
      // Cookie сначала: `_ym_uid` ставится ym-тэгом сразу при загрузке скрипта и это
      // и есть clientId. Fallback — кешированный getClientID-промис из install()
      // (резолвится в момент готовности тэга через ym-очередь): закрывает первый
      // заход, когда события уходят раньше, чем тэг успел поставить cookie.
      const clientId = getYmUidCookie() || (clientIdReady ? await clientIdReady : null);
      const yclid = getYclidFromStorage();
      const out = {};
      if (clientId) out.clientId = clientId;
      if (yclid) out.yclid = yclid;
      return out;
    },
  };
}
